<script>
  import {
    appIconUrl as sharedAppIconUrl,
    localAppIcon,
  } from "lib/util/appIcons.js";
  import { onMount } from "svelte";
  import SpotlightGuide from "@components/common/SpotlightGuide.svelte";
  import { registerViewTour } from "lib/stores/viewTourStore.js";
  import { useUrlParams } from "@stores/urlStore.js";
  import { PROJECT_STAGES } from "@utils/stagesAndSteps.js";
  import { projectListStore } from "@stores/damProjectsStore.js";
  import Modal from "lib/components/Modal.svelte";

  import { fetchWithSession } from "lib/util/fetchWithSession.js";
  import Header from "@components/Header.svelte";
  import ProjectStepActions from "./components/ProjectStepActions.svelte";
  import DataTableBlack from "@components/DataTableBlack.svelte";
  import {
    BadgeColor,
    InfoBlock,
    Tooltip,
    TextInput,
    SectionWrapper,
    Select,
    Button,
  } from "paxray-components-svelte";
  import { CLOSE_ICON, EDIT_ICON, SAVE_ICON, TRASH_ICON } from '@utils/icons.js';

  const urlParams = useUrlParams();

  const step = PROJECT_STAGES.SCOPING.steps.APPNAME_MAP;
  const stage = PROJECT_STAGES.SCOPING.id;

  const { getProjectLimitations, getProjectProgress } = projectListStore;

  let stage_id = "";

  const headers = [
    {
      key: "clearname",
      value: "Name",
      tooltip: "Readable application name shown in analyses.",
    },
    {
      key: "applicationname",
      value: "Technical Name/URL",
      tooltip: "Original application name or URL captured from the system.",
    },
    {
      key: "apptype",
      value: "Type",
      tooltip: "Application type used for grouping and coloring.",
    },
    {
      key: "category",
      value: "Category",
      tooltip:
        "What kind of software this is. Used by the software portfolio; empty means it is derived from name and type.",
    },
    {
      key: "menu",
      value: "Actions",
      width: "105px",
    },
  ];

  const aiReviewHeaders = [
    { key: "clearname", value: "Name", width: "25ch" },
    { key: "applicationname", value: "Technical Name/URL", width: "25ch" },
    { key: "apptype", value: "Type", width: "14ch" },
    { key: "category", value: "Category", width: "16ch" },
    { key: "icon", value: "Icon", width: "8ch" },
  ];

  const aiResultHeaders = [
    { key: "clearname", value: "Name", width: "25%" },
    { key: "applicationname", value: "Technical Name/URL", width: "30%" },
    { key: "changes", value: "Changes", width: "35%" },
  ];

  const selectedOptions = [
    { key: "withoutMapping", value: "Apps without Mapping" },
    { key: "withoutType", value: "Apps without Type" },
    { key: "main", value: "Main Apps" },
    { key: "supportive", value: "Support Apps" },
    { key: "communication", value: "Communication Apps" },
    { key: "unwanted", value: "Unwanted Apps" },
    { key: "excluded", value: "Excluded Apps" },
    { key: "aiUnresolved", value: "Checked by Pax AI, unresolved" },
  ];

  let mappings = [];
  let tableData = [];
  let applicationInfoLogs = [];
  let appnames = [];
  let clearnames = [];
  let apptypes = [];
  let categories = [];
  let categoryOptions = [];
  let editids = [];
  let editSnapshots = {};
  let newAppName = "";
  let newClearName = "";
  let newAppType = "";
  let manualAddModalOpen = false;
  let apptypelist = [
    { value: "main", label: "Main App", kpiLabel: "Main Apps" },
    {
      value: "supportive",
      key: "support",
      label: "Support App",
      kpiLabel: "Support Apps",
    },
    {
      value: "communication",
      label: "Communication App",
      kpiLabel: "Communication Apps",
    },
    { value: "unwanted", label: "Unwanted App", kpiLabel: "Unwanted Apps" },
    // Nicht in der finalen Whitelist: wird nach Abschluss der Whitelist automatisch
    // gesetzt (siehe declareExcludedApps) und ist im Dropdown waehlbar.
    {
      value: "excluded",
      label: "Excluded (not whitelisted)",
      kpiLabel: "Excluded Apps",
    },
  ];
  // Finale Whitelist (Scoping): technische Namen und Web-Domains
  let whitelistFinal = false;
  let whitelistNames = new Set();
  let whitelistDomains = [];
  let saveSuccessful;
  let page = 1;
  let pageSize = 10;
  let loading = true;
  let mappingTourMounted = false;
  let unregisterMappingTour;
  let showMappingGuide = false;
  let mappingGuideStep = 0;
  let mappingGuideSteps = [];

  onMount(() => {
    mappingTourMounted = true;
    return () => unregisterMappingTour?.();
  });

  $: if (mappingTourMounted && !loading) {
    if (!unregisterMappingTour)
      unregisterMappingTour = registerViewTour(startMappingGuide);
  } else {
    unregisterMappingTour?.();
    unregisterMappingTour = null;
    showMappingGuide = false;
  }

  function startMappingGuide() {
    const completed = Boolean(
      document.querySelector(
        '[data-guide="complete-appname-mapping"] .completed-badge',
      ),
    );
    mappingGuideSteps = [
      {
        target: '[data-guide="appname-mapping-overview"]',
        title: "Make application names easy to understand",
        text: "Give apps and websites readable names and assign application types to make your analyses easier to understand. This Scoping step is optional, and mappings can be updated later.",
      },
      {
        target: '[data-guide="appname-mapping-table"]',
        highlightSelector: '[data-guide="appname-mapping-filter"], .bx--search',
        title: "Find entries that need attention",
        text: "The list initially shows “Apps without Type”. Choose “Apps without Mapping” to find entries that still need a readable name, or select “All” to review the full list. Use search to find a specific app or website.",
      },
      {
        target: '[data-guide="appname-mapping-table"] thead',
        title: "Understand names, types and categories",
        text: "Name is the readable label shown in analyses. Technical Name/URL identifies the original app or website. Type describes its role, such as Main, Support or Communication. Category describes what kind of software it is; leave it as “Derived” to use an automatically determined category.",
      },
      {
        target: '[data-guide="appname-mapping-table"]',
        highlightSelector: '.menu_actions, [data-guide="appname-mapping-add"]',
        title: "Edit or add an application",
        text: "Click “Edit” on a row to change its details, then click “Save” to save your changes or “Cancel” to discard your edits. Use “Add App” to add an application that is missing from the list.",
      },
      ...(missingMappingCount > 0 || aiCheckedUnresolvedCount > 0
        ? [
            {
              target: '[data-guide="appname-mapping-ai"]',
              title: "Fill in missing information with Pax AI",
              text: aiSuggestionLoading
                ? "Pax AI is checking the selected apps. When the check finishes, review the suggested changes and click “Apply selected changes” to save the ones you want to keep. Suggestions are only saved after you confirm them."
                : missingMappingCount > 0
                  ? "Click “Generate missing information” and choose the apps you want Pax AI to check. Review the suggested changes, then click “Apply selected changes” to save the ones you want to keep. Suggestions are only saved after you confirm them. Use “Checked by Pax AI, unresolved” to find entries that still need manual review."
                  : "Click “Retry with Pax AI” to check unresolved entries again. Review any suggestions before applying them. If no useful information is found, use “Edit” to complete the details manually.",
            },
          ]
        : []),
      {
        target:
          '[data-guide="complete-appname-mapping"] > button, [data-guide="complete-appname-mapping"] > .completed-badge',
        title: "Complete the mapping step",
        text: completed
          ? "This mapping step is complete. You can still review, change or extend the mappings here and later during analysis. Save each edit to apply your changes."
          : "Review your mappings and save any open edits before clicking “Complete Appname Mapping”. Completing this step does not lock your mappings. You can still change or extend them later during analysis.",
      },
    ].filter(({ target }) => {
      const element = document.querySelector(target);
      return (
        element &&
        getComputedStyle(element).visibility === "visible" &&
        element.getBoundingClientRect().width > 0 &&
        element.getBoundingClientRect().height > 0
      );
    });
    mappingGuideStep = 0;
    showMappingGuide = mappingGuideSteps.length > 0;
  }
  let searchTerm = "";
  // Start with the existing "Apps without Type" filter selected.
  let selectedTypeFilter = "withoutType";
  let aiSuggestions = {};
  let aiSuggestionLoading = false;
  let aiSuggestionSaving = false;
  let aiSuggestionCancelRequested = false;
  let aiReviewModalOpen = false;
  let aiResultModalOpen = false;
  let selectedAiCandidateIds = [];
  let selectedAiSuggestionIds = [];
  let aiLoadMode = "delta";
  // { done, total } while a run is in flight, null otherwise.
  let aiProgress = null;

  // Ergebnis des letzten Pax-AI-Laufs fuer die Rueckmeldung im Dialog. Was
  // Pax AI geprueft hat, steht dauerhaft am Eintrag (ai_checked_at): ein
  // geprueft-ohne-Ergebnis-Eintrag zaehlt nicht mehr als offen, sonst kam die
  // Zahl im Banner nach dem Bestaetigen nie bei 0 an (Nutzerbefund 10.09.2026).
  let aiRunSummary = null;
  let aiNothingModalOpen = false;
  let aiIncludeChecked = false;
  let aiRunCandidateIds = [];
  $: aiCheckedUnresolvedCount = tableData.filter(isCheckedUnresolved).length;

  function isCheckedUnresolved(row) {
    return Boolean(row?.ai_checked_at) && needsAiSuggestion(row);
  }

  // Offen ist, was weder ausgefuellt noch von Pax AI geprueft ist.
  function isOpenForAi(row) {
    return needsAiSuggestion(row) && !row?.ai_checked_at;
  }

  async function markAiChecked(ids) {
    if (!ids.length) return;
    await fetchWithSession("/api/damprojects/markAppnameMappingAiChecked", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ids }),
    });
  }

  // Hat der Vorschlag gegenueber der Zeile etwas Neues?
  function suggestionChanges(row, suggestion) {
    if (!row || !suggestion) return false;
    const suggestedAppType = toAppTypeValue(suggestion.apptype);
    return Boolean(
      (suggestion.clearname && suggestion.clearname !== row.clearname) ||
        (suggestedAppType &&
          normalizeAppType(suggestedAppType) !==
            normalizeAppType(row.apptype)) ||
        (suggestion.category && suggestion.category !== row.category) ||
        (suggestion.website &&
          suggestion.website !== "-" &&
          suggestion.website !== row.icon_domain),
    );
  }
  // Applications per request. Matches the backend batch size, so each request
  // is one model call and the progress bar advances at a steady pace.
  const AI_BATCH_SIZE = 100;

  const paxbotStarterQuestions = [
    "Which mappings should I review first?",
    "What do the application types mean?",
    "Why is a clear name important?",
    "How does AI completion work?",
  ];
  const appnameMappingHelpContext = {
    task: "Map technical application names to readable names, assign application types, and identify entries that still need review.",
    starterQuestions: paxbotStarterQuestions,
  };

  $: apptypeSelectOptions = apptypelist.map((apptype) => ({
    value: apptype.value,
    text: apptype.label,
  }));

  // Leerwert zuerst: ohne gepflegte Kategorie leitet die Auswertung sie aus
  // Name und Typ ab.
  $: categorySelectOptions = [
    { value: "", text: "Derived" },
    ...categoryOptions.map((name) => ({ value: name, text: name })),
  ];

  $: selectedTypeSelectOptions = selectedOptions.map((option) => ({
    value: option.key,
    text: option.value,
  }));

  $: setTableData(mappings, applicationInfoLogs);

  $: suggestedTableData = tableData.map((row) =>
    withAiSuggestion(row, aiSuggestions),
  );
  $: aiReviewRows = tableData
    .filter((row) =>
      aiIncludeChecked ? needsAiSuggestion(row) : isOpenForAi(row),
    )
    .map((row) => ({
      id: row.id,
      applicationname: row.applicationname,
      clearname: needsClearName(row) ? "Missing or technical" : row.clearname,
      apptype: normalizeAppType(row.apptype)
        ? getAppTypeLabel(row.apptype)
        : "No Type",
      category: row.category || "Missing",
      icon: needsIcon(row) ? "Missing" : "OK",
    }));
  $: aiResultRows = tableData
    .filter((row) => aiSuggestions[row.applicationname])
    .map((row) => {
      const suggestion = aiSuggestions[row.applicationname];
      const suggestedAppType = toAppTypeValue(suggestion.apptype);
      const changes = [];
      if (suggestion.clearname && suggestion.clearname !== row.clearname) {
        changes.push(`Name → ${suggestion.clearname}`);
      }
      if (
        suggestedAppType &&
        normalizeAppType(suggestedAppType) !== normalizeAppType(row.apptype)
      ) {
        changes.push(`Type → ${getAppTypeLabel(suggestedAppType)}`);
      }
      if (suggestion.category && suggestion.category !== row.category) {
        changes.push(`Category → ${suggestion.category}`);
      }
      if (suggestion.website && suggestion.website !== row.icon_domain) {
        changes.push(
          suggestion.website === "-"
            ? "Icon → none available (initial letter)"
            : `Icon → ${suggestion.website}`,
        );
      }
      return {
        id: row.id,
        clearname: row.clearname || "Missing",
        applicationname: row.applicationname,
        changes: changes.join(" · ") || "No change",
      };
    });
  $: filteredMappings = suggestedTableData.filter((mapping) => {
    const search = searchTerm.trim().toLowerCase();
    const matchesSearch =
      !search ||
      `${mapping.applicationname || ""}`.toLowerCase().includes(search) ||
      `${mapping.clearname || ""}`.toLowerCase().includes(search);
    const matchesType =
      !selectedTypeFilter ||
      selectedTypeFilter === "all" ||
      (selectedTypeFilter === "withoutMapping" && isWithoutMapping(mapping)) ||
      (selectedTypeFilter === "withoutType" &&
        !normalizeAppType(mapping.apptype)) ||
      (selectedTypeFilter === "aiUnresolved" && isCheckedUnresolved(mapping)) ||
      normalizeAppType(mapping.apptype) ===
        normalizeAppType(selectedTypeFilter);

    return matchesSearch && matchesType;
  });
  $: aiCandidateCount =
    aiLoadMode === "clean"
      ? tableData.length
      : tableData.filter(isOpenForAi).length;
  $: missingMappingCount = tableData.filter(isOpenForAi).length;
  $: aiSuggestionCount = Object.keys(aiSuggestions).length;
  $: pageCount = Math.max(Math.ceil(filteredMappings.length / pageSize), 1);
  $: if (page > pageCount) {
    page = pageCount;
  }
  $: typeKpis = [
    { label: "Apps", count: tableData.length },
    {
      label: "Apps without Mapping",
      count: countAppsWithoutMapping(),
      showPercentage: true,
    },
    {
      label: "Apps without Type",
      count: countAppsWithoutType(),
      showPercentage: true,
    },
    ...apptypelist.map((apptype) => ({
      label: apptype.kpiLabel,
      count: countAppType(apptype.key || apptype.value),
      showPercentage: true,
    })),
  ];

  $: ({ idproject: routeProjectId } = $urlParams);
  $: hasExplicitProjectContext = Boolean(
    routeProjectId &&
      routeProjectId !== "null" &&
      routeProjectId !== "undefined",
  );
  // Keep the project selected in the SPA as a fallback for legacy links that
  // open this route without forwarding the query string.
  $: idproject =
    routeProjectId &&
    routeProjectId !== "null" &&
    routeProjectId !== "undefined"
      ? routeProjectId
      : $projectListStore.currentProjectId;
  $: hasProjectContext = Boolean(
    idproject && idproject !== "null" && idproject !== "undefined",
  );

  function createMapping(applicationname, clearname, apptype) {
    fetchWithSession(`/api/damprojects/createAppnameMapping`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        applicationname,
        clearname,
        apptype,
        project_stage_id: stage_id,
      }),
    })
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok)
          throw new Error(data.message || "Mapping could not be saved.");
        return data;
      })
      .then((data) => {
        mappings.push({
          applicationname,
          clearname,
          apptype,
          id: data.id,
        });
        mappings = mappings;
        clearAiSuggestion(applicationname);
      });
  }

  function addNewMapping() {
    if (!newAppName.trim() || !newClearName.trim()) {
      return;
    }
    createMapping(newAppName, newClearName, newAppType);
    newAppName = "";
    newClearName = "";
    newAppType = "";
    manualAddModalOpen = false;
  }

  function closeManualAddModal() {
    manualAddModalOpen = false;
    newAppName = "";
    newClearName = "";
    newAppType = "";
  }

  function deleteMapping(id) {
    if (!mappings.some((mapping) => mapping.id === id)) return;
    fetchWithSession(`/api/damprojects/deleteAppnameMapping`, {
      method: "POST",
      body: JSON.stringify({ id: id }),
    })
      .then((res) => res.json())
      .then((data) => {
        mappings = mappings.filter((mapping) => mapping.id != id);
        mappings = mappings;
        clearAiSuggestion(
          tableData.find((mapping) => mapping.id === id)?.applicationname,
        );
      });
  }

  function startEdit(row) {
    if (!editids.includes(row.id)) {
      editids.push(row.id);
      editids = editids;
    }
    editSnapshots[row.id] = {
      applicationname: row.applicationname,
      clearname: row.clearname,
      apptype: row.apptype,
      category: row.category,
    };
    editSnapshots = editSnapshots;
    appnames[row.id] = row.applicationname;
    clearnames[row.id] = row.clearname;
    apptypes[row.id] = row.apptype;
    categories[row.id] = row.category || "";
  }

  function cancelEdit(row) {
    const snapshot = editSnapshots[row.id];
    appnames[row.id] = snapshot?.applicationname ?? row.applicationname;
    clearnames[row.id] = snapshot?.clearname ?? row.clearname;
    apptypes[row.id] = snapshot?.apptype ?? row.apptype;
    categories[row.id] = snapshot?.category ?? row.category ?? "";
    editids = editids.filter((id) => id != row.id);
    delete editSnapshots[row.id];
    editSnapshots = editSnapshots;
  }

  function updateData(row) {
    const currentData = mappings.find((mapping) => mapping.id == row.id);
    if (!currentData) {
      createMapping(appnames[row.id], clearnames[row.id], apptypes[row.id]);
    } else if (
      currentData.applicationname !== appnames[row.id] ||
      currentData.clearname !== clearnames[row.id] ||
      currentData.apptype !== apptypes[row.id]
    ) {
      saveRow(row);
    }
  }

  function finishEdit(row) {
    updateData(row);
    editids = editids.filter((id) => id != row.id);
    delete editSnapshots[row.id];
    editSnapshots = editSnapshots;
  }

  function saveRow(row) {
    fetchWithSession(`/api/damprojects/saveAppnameMapping`, {
      method: "POST",
      body: JSON.stringify({
        id: row.id,
        applicationname: appnames[row.id],
        clearname: clearnames[row.id],
        apptype: apptypes[row.id],
        category: categories[row.id] || "",
        project_stage_id: stage_id,
      }),
    })
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok)
          throw new Error(data.message || "Mapping could not be saved.");
        return data;
      })
      .then((data) => {
        mappings.find((mapping) => mapping.id == row.id).applicationname =
          appnames[row.id];
        mappings.find((mapping) => mapping.id == row.id).clearname =
          clearnames[row.id];
        mappings.find((mapping) => mapping.id == row.id).apptype =
          apptypes[row.id];
        mappings.find((mapping) => mapping.id == row.id).category =
          categories[row.id] || "";
        mappings = mappings;
        clearAiSuggestion(row.applicationname);
      })
      .catch((error) => {
        console.error(error.message || "Mapping could not be saved.");
      });
  }

  function normalizeAppType(type) {
    const normalizedType = `${type || ""}`.trim().toLowerCase();
    if (!normalizedType || normalizedType === "no type") return "";
    return normalizedType === "supportive" ? "support" : normalizedType;
  }

  function appIconUrl(applicationname, iconDomain = "") {
    return sharedAppIconUrl(applicationname, { iconDomain });
  }

  function toAppTypeValue(type) {
    const normalizedType = `${type || ""}`.trim().toLowerCase();
    if (!normalizedType || normalizedType === "no type") return "";
    // Nur bekannte Schluessel gelangen in die Datenbank; ein unbekannter Text
    // (etwa "Support App" aus einer KI-Antwort) wird verworfen statt gespeichert.
    return (
      apptypelist.find((apptype) =>
        [apptype.value, apptype.key, apptype.label, apptype.kpiLabel]
          .filter(Boolean)
          .some((option) => option.toLowerCase() === normalizedType),
      )?.value || ""
    );
  }

  function needsClearName(row) {
    const technicalName = `${row?.applicationname || ""}`.trim();
    const clearName = `${row?.clearname || ""}`.trim();
    // Exact comparison: a clear name that only fixes capitalization, such as
    // "Notepad" for "notepad", is a finished mapping and must not be re-flagged.
    return !clearName || clearName === technicalName;
  }

  // Ausgeschlossene Anwendungen (nicht in der finalen Whitelist) sind fertig:
  // kein Klarname noetig, kein KI-Kandidat - sonst blieben sie dauerhaft
  // "missing", weil das Modell fuer Domains keinen anderen Namen liefert.
  function isExcluded(row) {
    return normalizeAppType(row?.apptype) === "excluded";
  }

  function hasRecorderIcon(applicationname) {
    return Boolean(
      applicationInfoLogs.find(
        (log) => log.application_name === applicationname,
      )?.image_data,
    );
  }

  function isWebsite(applicationname) {
    return `${applicationname || ""}`.includes(".");
  }

  // Ein Programm ohne Recorder-Bild und ohne hinterlegte Hersteller-Domain hat kein
  // Icon; Websites brauchen keine, ihr Favicon kommt ueber die eigene Domain.
  function needsIcon(row) {
    if (isExcluded(row) || isWebsite(row?.applicationname)) return false;
    if (localAppIcon(row?.applicationname, row?.clearname)) return false;
    return !row?.icon_domain && !hasRecorderIcon(row?.applicationname);
  }

  // Systemrauschen (unwanted) hat keine Kategorie - Pax AI laesst sie dort
  // absichtlich leer, also fehlt auch nichts.
  function needsCategory(row) {
    return !row?.category && normalizeAppType(row?.apptype) !== "unwanted";
  }

  function needsAiSuggestion(row) {
    if (isExcluded(row)) return false;
    return (
      needsClearName(row) ||
      !normalizeAppType(row?.apptype) ||
      needsCategory(row) ||
      needsIcon(row)
    );
  }

  function withAiSuggestion(row, suggestions) {
    const suggestion = suggestions[row.applicationname];
    if (!suggestion) return row;
    const aiSuggestedFields = [];
    const suggestedAppType = toAppTypeValue(suggestion.apptype);
    if (suggestion.clearname) aiSuggestedFields.push("clearname");
    if (suggestedAppType) aiSuggestedFields.push("apptype");
    if (suggestion.website) aiSuggestedFields.push("icon_domain");
    if (suggestion.category) aiSuggestedFields.push("category");
    return {
      ...row,
      clearname: suggestion.clearname || row.clearname,
      apptype: suggestedAppType || row.apptype,
      category: suggestion.category || row.category,
      icon_domain: suggestion.website || row.icon_domain,
      aiSuggestedFields,
    };
  }

  function clearAiSuggestion(applicationname) {
    if (!aiSuggestions[applicationname]) return;
    const nextSuggestions = { ...aiSuggestions };
    delete nextSuggestions[applicationname];
    aiSuggestions = nextSuggestions;
  }

  function countAppType(type) {
    const normalizedType = normalizeAppType(type);
    return tableData.filter(
      (mapping) => normalizeAppType(mapping.apptype) === normalizedType,
    ).length;
  }

  function isWithoutMapping(row) {
    if (isExcluded(row)) return false;
    return (
      !mappings.some((mapping) => mapping.id === row.id) || needsClearName(row)
    );
  }

  function countAppsWithoutMapping() {
    return tableData.filter((mapping) => isWithoutMapping(mapping)).length;
  }

  function countAppsWithoutType() {
    return tableData.filter((mapping) => !normalizeAppType(mapping.apptype))
      .length;
  }

  function formatTypePercentage(count) {
    if (!tableData.length) return "0%";
    return `${Math.round((count / tableData.length) * 100)}%`;
  }

  function formatInteger(value) {
    return new Intl.NumberFormat("de-DE").format(value);
  }

  function getAppTypeLabel(value) {
    const normalizedValue = normalizeAppType(value);
    if (!normalizedValue) return "No Type";
    return (
      apptypelist.find(
        (apptype) => normalizeAppType(apptype.value) === normalizedValue,
      )?.label || value
    );
  }

  function getOriginalClearName(row) {
    return applicationInfoLogs.find(
      (log) => log.application_name === row.applicationname,
    )?.clear_name;
  }

  async function generateAiSuggestions() {
    if (aiSuggestionLoading || aiSuggestionSaving || !tableData.length) return;
    aiSuggestionCancelRequested = false;
    aiSuggestionLoading = true;
    try {
      // AI completion is intentionally restricted to Delta Load for now.
      aiLoadMode = "delta";
      const cleanLoad = aiLoadMode === "clean";
      // Delta load only sends rows that actually need a name or type — the same
      // filter aiCandidateCount uses. Every candidate is sent: the backend
      // splits them into batches, so the catalog size never truncates a run.
      const candidates = cleanLoad
        ? tableData
        : tableData.filter(
            (row) =>
              selectedAiCandidateIds.some(
                (id) => String(id) === String(row.id),
              ) && needsAiSuggestion(row),
          );
      const examples = cleanLoad
        ? []
        : tableData
            .filter(
              (row) => !needsClearName(row) && normalizeAppType(row.apptype),
            )
            .slice(0, 25);

      // One request per batch instead of one long request for everything, so
      // the user sees real progress. Names already agreed on travel along as
      // examples, which keeps variants of one product named consistently.
      const generatedSuggestions = [];
      const agreedNames = [];
      const seenSuggestions = new Set();
      aiProgress = { done: 0, total: candidates.length };

      for (let start = 0; start < candidates.length; start += AI_BATCH_SIZE) {
        if (aiSuggestionCancelRequested) {
          return;
        }
        const batch = candidates.slice(start, start + AI_BATCH_SIZE);
        const response = await fetchWithSession(
          "/api/damprojects/paxbot/appname-mapping-suggestions",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              applications: batch.map((row) => ({
                applicationname: row.applicationname,
                clearname: row.clearname,
                apptype: row.apptype,
                needsClearname: cleanLoad || needsClearName(row),
                needsType: cleanLoad || !normalizeAppType(row.apptype),
                // Programme ohne Icon: Hersteller-Domain erbitten, deren Favicon dient
                // als Icon (Websites tragen ihre Domain schon im Namen)
                needsIcon: cleanLoad
                  ? !isWebsite(row.applicationname)
                  : needsIcon(row),
                needsCategory: cleanLoad || needsCategory(row),
                category: row.category || "",
              })),
              examples: [...examples, ...agreedNames.slice(-25)],
              context: {
                projectId: idproject,
                projectStageId: stage_id,
                loadMode: aiLoadMode,
              },
            }),
          },
        );
        const result = await response.json();
        if (aiSuggestionCancelRequested) {
          return;
        }
        if (!response.ok || result.status === "error") {
          throw new Error(result.message || "Pax AI request failed");
        }
        for (const suggestion of result.suggestions || []) {
          // The backend only deduplicates within one request, so keep the
          // first answer for a name that appeared in an earlier batch too.
          if (seenSuggestions.has(suggestion.applicationname)) continue;
          seenSuggestions.add(suggestion.applicationname);
          generatedSuggestions.push(suggestion);
          if (suggestion.clearname && suggestion.apptype) {
            agreedNames.push(suggestion);
          }
        }
        // Icon erbeten, aber keine Quelle genannt (Systemkomponenten, "C:"): das
        // ist eine Entscheidung, kein Fehler - als "kein Icon" ("-") festhalten,
        // sonst bleibt die Anwendung dauerhaft in der Liste der fehlenden.
        for (const row of batch) {
          if (!needsIcon(row) || cleanLoad) continue;
          const existing = generatedSuggestions.find(
            (suggestion) => suggestion.applicationname === row.applicationname,
          );
          if (existing) {
            if (!existing.website) existing.website = "-";
          } else {
            seenSuggestions.add(row.applicationname);
            generatedSuggestions.push({
              applicationname: row.applicationname,
              website: "-",
            });
          }
        }
        aiProgress = {
          done: Math.min(start + batch.length, candidates.length),
          total: candidates.length,
        };
      }

      aiSuggestions = generatedSuggestions.reduce(
        (suggestions, suggestion) => ({
          ...suggestions,
          [suggestion.applicationname]: suggestion,
        }),
        {},
      );
      const byName = new Map(
        candidates.map((row) => [row.applicationname, row]),
      );
      const changed = new Set(
        generatedSuggestions
          .filter((suggestion) =>
            suggestionChanges(
              byName.get(suggestion.applicationname),
              suggestion,
            ),
          )
          .map((suggestion) => suggestion.applicationname),
      );
      aiRunCandidateIds = candidates.map((row) => row.id).filter(Boolean);
      aiRunSummary = {
        checked: candidates.length,
        suggested: changed.size,
        unresolved: candidates
          .filter((row) => !changed.has(row.applicationname))
          .map((row) => row.applicationname),
      };
      // "Kein Icon verfuegbar" ist ebenfalls ein Ergebnis, das der Nutzer
      // uebernehmen kann - danach zaehlt die Anwendung nicht mehr als offen.
      const hasResults = generatedSuggestions.some(
        (suggestion) =>
          changed.has(suggestion.applicationname) || suggestion.website === "-",
      );
      if (hasResults) {
        selectedAiSuggestionIds = generatedSuggestions
          .map(
            (suggestion) =>
              tableData.find(
                (row) => row.applicationname === suggestion.applicationname,
              )?.id,
          )
          .filter(Boolean);
        aiResultModalOpen = true;
      } else {
        // Nichts zu bestaetigen: die geprueften Eintraege sofort als geprueft
        // vermerken, damit die Zahl im Banner auf 0 geht.
        await markAiChecked(aiRunCandidateIds);
        await getAppnameMapping();
        setTableData();
        aiNothingModalOpen = true;
      }
    } catch (error) {
      console.error(error.message || "AI suggestions could not be generated.");
    } finally {
      aiSuggestionLoading = false;
      aiProgress = null;
    }
  }

  function cancelAiSuggestions() {
    if (!aiSuggestionLoading) return;
    aiSuggestionCancelRequested = true;
  }

  function openAiReviewModal() {
    // Ohne Projekt (uebergreifende Pflege) gibt es keine Stufe; der Vorschlag
    // laeuft trotzdem, das Backend nimmt dann die Blacklists aller Projekte.
    if (aiSuggestionLoading) return;
    selectedAiCandidateIds = aiReviewRows.map((row) => row.id);
    aiReviewModalOpen = true;
  }

  function startAiSuggestions() {
    aiReviewModalOpen = false;
    if (!selectedAiCandidateIds.length) return;
    generateAiSuggestions();
  }

  function discardAiSuggestions() {
    aiResultModalOpen = false;
    selectedAiSuggestionIds = [];
    aiSuggestions = {};
  }

  async function persistAiSuggestion(row) {
    const apptype = toAppTypeValue(row.apptype);
    const existingMapping = mappings.find(
      (mapping) => mapping.applicationname === row.applicationname,
    );
    const response = await fetchWithSession(
      existingMapping
        ? "/api/damprojects/saveAppnameMapping"
        : "/api/damprojects/createAppnameMapping",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: existingMapping?.id,
          applicationname: row.applicationname,
          clearname: row.clearname,
          apptype,
          ...(row.category ? { category: row.category } : {}),
          ...(row.icon_domain ? { icon_domain: row.icon_domain } : {}),
          project_stage_id: stage_id,
        }),
      },
    );
    if (!response.ok) throw new Error("An AI suggestion could not be saved.");
    if (existingMapping) {
      existingMapping.clearname = row.clearname;
      existingMapping.apptype = apptype;
      if (row.icon_domain) existingMapping.icon_domain = row.icon_domain;
      mappings = mappings;
    }
    clearAiSuggestion(row.applicationname);
  }

  async function saveAiSuggestions() {
    if (aiSuggestionSaving || !aiSuggestionCount) return;
    aiSuggestionSaving = true;
    const rows = suggestedTableData.filter(
      (row) =>
        aiSuggestions[row.applicationname] &&
        selectedAiSuggestionIds.some((id) => String(id) === String(row.id)),
    );
    if (!rows.length) {
      aiResultModalOpen = false;
      aiSuggestions = {};
      return;
    }
    aiResultModalOpen = false;
    try {
      for (let index = 0; index < rows.length; index += 10) {
        await Promise.all(
          rows.slice(index, index + 10).map(persistAiSuggestion),
        );
      }
      await markAiChecked(aiRunCandidateIds);
      await getAppnameMapping();
      setTableData();
    } catch (error) {
      await getAppnameMapping();
      setTableData();
    } finally {
      aiSuggestionSaving = false;
    }
  }

  // Mit Projektstufe liefert das Backend alle beobachteten Anwendungen; ohne
  // Stufe nur die in irgendeiner Whitelist bestaetigten. Hier muessen auch die
  // ausgeschlossenen sichtbar sein, sonst legt declareExcludedApps sie erneut an
  // und laeuft je Anwendung in einen 500er (Nutzerbefund 16.09.2026).
  async function getAppnameMapping() {
    const query = stage_id
      ? `?project_stage_id=${encodeURIComponent(stage_id)}`
      : "";
    const res = await fetchWithSession(
      `/api/damprojects/getAppnameMapping${query}`,
    );
    if (!res.ok) throw new Error("Appname mappings could not be loaded.");
    const data = await res.json();
    mappings = data;
  }

  async function getAppnameCategories() {
    const res = await fetchWithSession("/api/damprojects/appname-categories");
    if (!res.ok) return;
    const data = await res.json();
    if (Array.isArray(data)) categoryOptions = data;
  }

  async function getApplicationInfoLogs(stageIds = [stage_id]) {
    const responses = await Promise.all(
      stageIds
        .filter(Boolean)
        .map((stageId) =>
          fetchWithSession(
            `/api/damprojects/get-application-info-logs/${stageId}`,
          ),
        ),
    );
    if (responses.some((res) => !res.ok))
      throw new Error("Application information could not be loaded.");

    const logs = (await Promise.all(responses.map((res) => res.json()))).flat();
    applicationInfoLogs = Array.from(
      new Map(logs.map((log) => [log.application_name, log])).values(),
    );
  }

  async function loadWhitelist() {
    const progress = $projectListStore.currentProjectProgress || {};
    const scoping = (progress.project_stages || []).find(
      (projectStage) => projectStage.stage === "SCOPING",
    );
    whitelistFinal = Boolean(
      (scoping?.project_steps || []).find(
        (projectStep) => projectStep.id === "END_WHTLST",
      )?.completed,
    );
    const limitationId = $projectListStore.idLimitation;
    if (!whitelistFinal || !limitationId) return;
    const res = await fetchWithSession(
      `/api/damprojects/DAMLimitations/${limitationId}`,
    );
    if (!res.ok) return;
    const data = await res.json();
    if (`${data.type || ""}`.toLowerCase() !== "whitelist") return;
    const names = new Set();
    const domains = [];
    (data.items || []).forEach((item) => {
      if (item.isbrowser) {
        const domain = `${item.url || item.name || ""}`.trim().toLowerCase();
        if (domain) domains.push(domain);
      } else {
        const name = `${item.appname || item.name || ""}`.trim().toLowerCase();
        if (name) names.add(name);
      }
    });
    whitelistNames = names;
    whitelistDomains = domains;
  }

  // Ein Fensterlog-Name ist freigegeben, wenn er einer Whitelist-Anwendung entspricht
  // oder (Website) auf eine freigegebene Domain endet: "www.google.com" zu "google.com".
  function isWhitelisted(applicationname) {
    const name = `${applicationname || ""}`.trim().toLowerCase();
    if (!name) return true;
    if (whitelistNames.has(name)) return true;
    return whitelistDomains.some(
      (domain) => name === domain || name.endsWith(`.${domain}`),
    );
  }

  // Nach Abschluss der Whitelist: alles, was nicht freigegeben ist, traegt den Typ
  // "excluded" - sonst stehen im Mapping Anwendungen ohne erkennbaren Grund neben
  // den Whitelist-Anwendungen. Eine vorhandene Zuordnung wird nur im Typ geaendert.
  async function declareExcludedApps() {
    if (!whitelistFinal || (!whitelistNames.size && !whitelistDomains.length))
      return;
    const candidates = tableData.filter(
      (row) =>
        !isWhitelisted(row.applicationname) &&
        normalizeAppType(row.apptype) !== "excluded",
    );
    for (const row of candidates) {
      const mapping = mappings.find((item) => item.id === row.id);
      const clearname = row.clearname || row.applicationname;
      const body = mapping
        ? {
            id: row.id,
            applicationname: row.applicationname,
            clearname,
            apptype: "excluded",
            project_stage_id: stage_id,
          }
        : {
            applicationname: row.applicationname,
            clearname,
            apptype: "excluded",
            project_stage_id: stage_id,
          };
      const res = await fetchWithSession(
        `/api/damprojects/${mapping ? "saveAppnameMapping" : "createAppnameMapping"}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        },
      );
      if (!res.ok) continue;
      const data = await res.json();
      if (mapping) {
        mapping.apptype = "excluded";
        mapping.clearname = clearname;
      } else {
        mappings.push({
          applicationname: row.applicationname,
          clearname,
          apptype: "excluded",
          id: data.id,
        });
      }
    }
    if (candidates.length) mappings = mappings;
  }

  function setTableData() {
    tableData = [];

    const projectApplicationNames = new Set(
      applicationInfoLogs.map((log) => log.application_name),
    );
    const visibleMappings = hasProjectContext
      ? mappings.filter((mapping) =>
          projectApplicationNames.has(mapping.applicationname),
        )
      : mappings;

    visibleMappings.forEach((mapping) => {
      tableData.push(mapping);
    });

    applicationInfoLogs.forEach((log) => {
      let mappingMatch = tableData.find(
        (mapping) => mapping.applicationname === log.application_name,
      );
      if (!mappingMatch) {
        tableData.push({
          applicationname: log.application_name,
          clearname: log.clear_name,
          apptype: "",
          id: log.application_name,
        });
      }
    });
  }

  onMount(async () => {
    loading = true;
    try {
      // Die Kategorien gelten fuer beide Wege - mit und ohne Projekt.
      await getAppnameCategories();
      if (!hasProjectContext) {
        await getAppnameMapping();
        return;
      }
      await Promise.all([
        getProjectProgress(idproject, { force: true }),
        getProjectLimitations(idproject, stage),
      ]);
      stage_id = projectListStore.getProjectStageId(stage);

      if (!stage_id) throw new Error("Project stage could not be loaded.");
      const projectStageIds = (
        $projectListStore.currentProjectProgress?.project_stages || []
      ).map((projectStage) => projectStage.id);
      await Promise.all([
        getAppnameMapping(),
        getApplicationInfoLogs(projectStageIds),
        loadWhitelist(),
      ]);
      setTableData();
      await declareExcludedApps();
    } catch (error) {
      console.error(error.message || "Appname mappings could not be loaded.");
    } finally {
      loading = false;
    }
  });
</script>

<Header
  section="Name Apps & Websites"
  area="Scoping"
  breadcrumb="Name Apps & Websites"
  tooltipText="Map technical application names to readable names and assign app types for analysis. This step is optional and mappings can be changed later."
>
  {#if hasExplicitProjectContext}
    <div data-guide="complete-appname-mapping">
      <ProjectStepActions
        {idproject}
        {step}
        stage={PROJECT_STAGES.SCOPING.id}
        showGoBackButton={false}
        bind:saveSuccessful
        text="Complete Appname Mapping"
        size="sm"
        completionWarning="This completes the current appname mapping step. You can still change or extend the mappings later during the analysis if needed."
      />
    </div>
  {/if}
</Header>

<SectionWrapper
  fullscreen
  shrinkContent
  seamless={true}
  seamlessPadding={true}
  {loading}
  loadingTitle="Loading relevant applications."
  loadingHint="Please wait while the applications for this project are identified."
  helpContext={appnameMappingHelpContext}
>
  <section slot="main" class="main">
    <div data-guide="appname-mapping-overview">
      <InfoBlock
        type="info"
        heading="Appname-Mapping"
        message="Assign readable names and app types below. Select &quot;Complete Appname Mapping&quot; when you are done."
        showClose={false}
      />
    </div>
    {#if missingMappingCount > 0 || aiCheckedUnresolvedCount > 0}
      <div data-guide="appname-mapping-ai">
        <InfoBlock
          type={aiSuggestionLoading ? "loading" : "paxAI"}
          heading={missingMappingCount > 0
            ? `${missingMappingCount} app${missingMappingCount === 1 ? "" : "s"} missing a name, type, category or icon`
            : `All apps completed - ${aiCheckedUnresolvedCount} checked by Pax AI without result`}
          message={missingMappingCount > 0
            ? `Pax AI can automatically fill in the missing names, types and icons.${aiCheckedUnresolvedCount ? ` ${aiCheckedUnresolvedCount} more were already checked without result (filter "Checked by Pax AI, unresolved").` : ""}`
            : "Their technical names carry no product hint. Window titles from recordings would help; otherwise fill them in manually, exclude them, or let Pax AI retry."}
          showClose={false}
          showActions={true}
          loading={aiSuggestionLoading}
          progress={aiProgress}
        >
          <div slot="cancel">
            <Button
              text="Cancel"
              kind="paxAI"
              size="default"
              on:click={cancelAiSuggestions}
            />
          </div>
          <div slot="actions">
            <Button
              text={missingMappingCount > 0
                ? "Generate missing information"
                : "Retry with Pax AI"}
              kind="paxAI"
              disabled={aiSuggestionLoading}
              on:click={() => {
                aiIncludeChecked = missingMappingCount === 0;
                openAiReviewModal();
              }}
            />
          </div>
        </InfoBlock>
      </div>
    {/if}
    <!--
    <div class="ai-assist-bar" class:review={aiSuggestionCount > 0}>
      <img src="/icons/bot.svg" alt="" />
      <div>
        <strong>
          {aiSuggestionCount
            ? `${aiSuggestionCount} AI suggestions ready for review`
            : "Complete mappings with Pax AI"}
        </strong>
        <span>
          {#if aiProgress}
            Processing {aiProgress.done} of {aiProgress.total} applications.
            <progress
              class="ai-progress"
              max={aiProgress.total}
              value={aiProgress.done}
            />
          {:else if !stage_id}
            Open this view from a project to use AI completion and project
            blacklist protection.
          {:else if aiSuggestionCount}
            Suggested values are highlighted in the table and remain unsaved
            until you approve them.
          {:else if aiLoadMode === "clean"}
            Rebuild clear names and types for all {aiCandidateCount} applications
            in this mapping list.
          {:else if aiCandidateCount}
            Add {aiCandidateCount} missing names or types and match new technical
            variants to existing application groups.
          {:else}
            All applications already have a clear name and type. Use Clean Load
            to rebuild them.
          {/if}
        </span>
      </div>
      <div class="ai-assist-actions">
        {#if aiSuggestionCount}
          <Button
            text="Discard"
            kind="outline"
            size="field"
            disabled={aiSuggestionSaving}
            on:click={() => (aiSuggestions = {})}
          />
          <Button
            text={aiSuggestionSaving ? "Saving..." : "Save all changes"}
            kind="secondary"
            size="field"
            disabled={aiSuggestionSaving}
            icon={CHECK_ICON}
            iconLeft
            on:click={saveAiSuggestions}
          />
        {:else}
          <div
            class="ai-load-mode"
            role="radiogroup"
            aria-label="AI mapping load mode"
          >
            <button
              type="button"
              role="radio"
              aria-checked={aiLoadMode === "delta"}
              class:active={aiLoadMode === "delta"}
              disabled={aiSuggestionLoading}
              title="Keep completed mappings and fill only missing or technical values"
              on:click={() => (aiLoadMode = "delta")}
            >
              Delta Load
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={aiLoadMode === "clean"}
              class:active={aiLoadMode === "clean"}
              disabled={aiSuggestionLoading}
              title="Rebuild clear names and types for the complete mapping list"
              on:click={() => (aiLoadMode = "clean")}
            >
              Clean Load
            </button>
          </div>
          <Button
            text={aiSuggestionLoading
              ? "Generating..."
              : `Run ${aiLoadMode === "clean" ? "Clean Load" : "Delta Load"}`}
            kind="secondary"
            size="field"
            disabled={aiSuggestionLoading ||
              aiCandidateCount === 0 ||
              editids.length > 0}
            icon={ROBOT_2_ICON}
            iconLeft
            on:click={generateAiSuggestions}
          />
        {/if}
      </div>
    </div>
    -->
    <div class="mapping-table-wrapper" data-guide="appname-mapping-table">
      <DataTableBlack
        {headers}
        rows={filteredMappings}
        sortable
        sortKey="clearname"
        sortDirection="ascending"
        searchValue={searchTerm}
        slicePage
        showNewButton={false}
        bind:page
        bind:pageSize
        totalItems={filteredMappings.length}
        pageSizes={[10, 25, 50, 100]}
        newButtonIconLeft={false}
        on:search={(e) => (searchTerm = e.detail.value)}
      >
        <svelte:fragment slot="cell" let:cell let:row>
          {#if cell.key === "menu"}
            <div class="menu_actions">
              {#if editids.includes(row.id)}
                <Button
                kind="secondary"
                  size="small"
                  icon={SAVE_ICON}
                  iconDescription="Save"
                  on:click={() => finishEdit(row)}
                />
                <Button
                  kind="secondary"
                  size="small"
                  icon={CLOSE_ICON}
                  iconDescription="Cancel"
                  on:click={() => cancelEdit(row)}
                />
              {:else}
                <Button
                  kind="secondary"
                  size="small"
                  icon={EDIT_ICON}
                  iconDescription="Edit"
                  on:click={() => startEdit(row)}
                />
                <Button
                  kind="secondary"
                  size="small"
                  icon={TRASH_ICON}
                  iconDescription="Delete"
                  disable={!mappings.some((mapping) => mapping.id === row.id)}
                  on:click={() => deleteMapping(row.id)}
                />
              {/if}
            </div>
          {:else if editids.includes(row.id)}
            {#if cell.key === "applicationname"}
              <TextInput bind:value={appnames[row.id]} />
            {:else if cell.key === "clearname"}
              <div class="mapping-name-cell">
                <img
                  class="mapping-app-icon"
                  src={appIconUrl(
                    appnames[row.id] || row.applicationname,
                    row.icon_domain,
                  )}
                  alt=""
                  aria-hidden="true"
                />
                <TextInput bind:value={clearnames[row.id]} />
              </div>
            {:else if cell.key === "apptype"}
              <Select
                items={apptypeSelectOptions}
                bind:selected={apptypes[row.id]}
                color="dark"
                noLabel
                placeholder="No Type"
              />
            {:else if cell.key === "category"}
              <Select
                items={categorySelectOptions}
                bind:selected={categories[row.id]}
                color="dark"
                noLabel
                placeholder="Derived"
              />
            {:else}
              {cell.value}
            {/if}
          {:else if cell.key === "clearname" && row.aiSuggestedFields?.includes("clearname")}
            <div class="mapping-name-cell">
              <img
                class="mapping-app-icon"
                src={appIconUrl(row.applicationname, row.icon_domain)}
                alt=""
                aria-hidden="true"
              />
              <div class="ai_suggested_value">
                <span>{cell.value || ""}</span>
                <small>AI suggestion</small>
              </div>
            </div>
          {:else if cell.key === "apptype" && row.aiSuggestedFields?.includes("apptype")}
            <div class="ai_suggested_value">
              <span>{getAppTypeLabel(cell.value)}</span>
              <small>AI suggestion</small>
            </div>
          {:else if cell.key === "category" && row.aiSuggestedFields?.includes("category")}
            <div class="ai_suggested_value">
              <span>{cell.value || ""}</span>
              <small>AI suggestion</small>
            </div>
          {:else if cell.key === "category" && !cell.value}
            <span class="derived_category">Derived</span>
          {:else if cell.key === "apptype" && (row.apptype === "No Type" || !row.apptype)}
            <BadgeColor text={"No Type"} bold small color="light-red" />
          {:else if cell.key === "clearname" && getOriginalClearName(row) && getOriginalClearName(row) !== row.clearname}
            <div class="mapping-name-cell">
              <img
                class="mapping-app-icon"
                src={appIconUrl(row.applicationname, row.icon_domain)}
                alt=""
                aria-hidden="true"
              />
              <div class="edited_clearname">
                {cell.value || ""}
                <Tooltip text={`Original: ${getOriginalClearName(row)}`} />
              </div>
            </div>
          {:else if cell.key === "clearname"}
            <div class="mapping-name-cell">
              <img
                class="mapping-app-icon"
                src={appIconUrl(row.applicationname, row.icon_domain)}
                alt=""
                aria-hidden="true"
              />
              <span class="mapping-text" title={cell.value || ""}
                >{cell.value || ""}</span
              >
              {#if isCheckedUnresolved(row)}
                <small
                  class="ai_unresolved"
                  title="Pax AI checked this entry and found no information in its technical name"
                  >Pax AI: nothing found</small
                >
              {/if}
            </div>
          {:else if cell.key === "applicationname"}
            <span
              class="mapping-text mapping-text-technical"
              title={cell.value || ""}>{cell.value || ""}</span
            >
          {:else}
            {cell.value || ""}
          {/if}
        </svelte:fragment>
        <div
          slot="left-toolbar"
          data-guide="appname-mapping-filter"
          style="display: flex; align-items: center; gap: var(--control-gap);"
        >
          <Select
            items={selectedTypeSelectOptions}
            bind:selected={selectedTypeFilter}
            color="dark"
            noLabel
            placeholder="All"
          />
        </div>
        <div slot="right-toolbar" data-guide="appname-mapping-add">
          <Button
            text="Add App"
            kind="secondary"
            on:click={() => (manualAddModalOpen = true)}
          />
        </div>
      </DataTableBlack>
    </div>
  </section>
</SectionWrapper>

<SpotlightGuide
  bind:open={showMappingGuide}
  bind:currentStep={mappingGuideStep}
  steps={mappingGuideSteps}
  allowInteraction={false}
/>

<Modal
  bind:open={manualAddModalOpen}
  modalHeading="Add App"
  subtitle="Add an application that was not detected by the paxray client."
  actions
  autowidth
  primaryButtonText="Save"
  secondaryButtonText="Cancel"
  primaryButtonAction={addNewMapping}
  secondaryButtonAction={closeManualAddModal}
>
  <div class="manual-add-form">
    <TextInput
      labelText="Technical Name"
      placeholder="e.g. winword for Microsoft Word"
      bind:value={newAppName}
    />
    <TextInput
      labelText="Clear Name"
      placeholder="Human readable name like 'Microsoft Word'"
      bind:value={newClearName}
    />
    <Select
      items={apptypeSelectOptions}
      bind:selected={newAppType}
      color="dark"
      labelText="Type"
      placeholder="No Type"
    />
  </div>
</Modal>

<Modal
  bind:open={aiReviewModalOpen}
  modalHeading="Choose apps to complete"
  subtitle="Select the apps where missing names, types, categories or icons should be filled in automatically."
  actions
  autowidth
  primaryButtonText="Complete selected apps"
  secondaryButtonText="Cancel"
  primaryButtonAction={startAiSuggestions}
  secondaryButtonAction={() => (aiReviewModalOpen = false)}
>
  {#if aiCheckedUnresolvedCount > 0}
    <label class="ai-include-checked">
      <input type="checkbox" bind:checked={aiIncludeChecked} />
      <span
        >Include the {aiCheckedUnresolvedCount} entr{aiCheckedUnresolvedCount ===
        1
          ? "y"
          : "ies"} Pax AI already checked without result</span
      >
    </label>
  {/if}
  <div class="ai-review-table-scroll">
    <DataTableBlack
      headers={aiReviewHeaders}
      rows={aiReviewRows}
      sortable={false}
      showToolbar={false}
      showPagination
      showNewButton={false}
      totalItems={aiReviewRows.length}
      batchSelection
      bind:selectedRowIds={selectedAiCandidateIds}
      slicePage
      pageSize={10}
      pageSizes={[10]}
    />
  </div>
</Modal>

<Modal
  bind:open={aiResultModalOpen}
  modalHeading="Review suggested changes"
  subtitle="Choose which suggested changes should be added to the appname mapping."
  actions
  autowidth
  primaryButtonText="Apply selected changes"
  secondaryButtonText="Discard suggestions"
  primaryButtonAction={saveAiSuggestions}
  secondaryButtonAction={discardAiSuggestions}
>
  <div class="ai-review-table-scroll">
    <DataTableBlack
      headers={aiResultHeaders}
      rows={aiResultRows}
      sortable={false}
      showToolbar={false}
      showPagination
      showNewButton={false}
      totalItems={aiResultRows.length}
      batchSelection
      bind:selectedRowIds={selectedAiSuggestionIds}
      slicePage
      pageSize={10}
      pageSizes={[10]}
    />
  </div>
</Modal>

<Modal
  bind:open={aiNothingModalOpen}
  modalHeading="Pax AI found no new information"
  actions
  noSecondaryButton
  primaryButtonText="OK"
  primaryButtonAction={() => (aiNothingModalOpen = false)}
>
  {#if aiRunSummary}
    <p class="ai-nothing">
      Pax AI checked {aiRunSummary.checked} app{aiRunSummary.checked === 1
        ? ""
        : "s"} and could not derive a name, type or category for {aiRunSummary
        .unresolved.length} of them. Their technical names carry no product hint
      - typically internal processes, servers or hosts
      {#if aiRunSummary.unresolved.length}
        (for example {aiRunSummary.unresolved.slice(0, 5).join(", ")})
      {/if}.
    </p>
    <p class="ai-nothing">
      Window titles from recordings serve Pax AI as hints; where none exist on
      this instance, the entries stay as they are. They are marked "Pax AI:
      nothing found", no longer count as open, and can be found under the filter
      "Checked by Pax AI, unresolved". Fill them in manually, exclude them, or
      retry later.
    </p>
  {/if}
</Modal>

<style>
  .ai-nothing {
    margin: 0 0 0.75rem;
    line-height: 1.45;
  }
  .ai-include-checked {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin: 0 0 0.75rem;
    font-size: 0.85rem;
  }
  .ai_unresolved {
    margin-left: 0.4rem;
    font-size: 0.7rem;
    color: var(--pax-grey-60, #8d8d8d);
    white-space: nowrap;
  }

  /* Ohne gepflegte Kategorie steht dort, woher sie kommt - zurueckhaltend,
     weil es kein Fehlerzustand ist. */
  .derived_category {
    color: var(--pax-grey-60, #8d8d8d);
    font-style: italic;
  }

  /* Pax-AI-Modale: Tabelle darf breiter sein als das Modal und wird dann
     horizontal gescrollt statt Spalten zu verschlucken (Nutzerwunsch 03.09.). */
  .ai-review-table-scroll {
    max-width: none;
    overflow-x: auto;
  }

  .ai-review-table-scroll :global(.bx--data-table) {
    width: 100%;
    min-width: max-content;
    table-layout: auto;
  }

  .ai-review-table-scroll :global(.bx--data-table th),
  .ai-review-table-scroll :global(.bx--data-table td) {
    max-width: 25ch;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .main {
    max-width: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .mapping-table-wrapper {
    margin-bottom: 0;
  }

  .mapping-table-wrapper :global(.custom-table .bx--toolbar-content) {
    gap: var(--control-gap);
    margin-bottom: var(--control-gap);
  }

  .mapping-name-cell {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    min-width: 0;
  }

  /* Lizenz- und Servernamen werden lang; die Zelle kuerzt mit "...", der
     volle Text steht im Tooltip (title). */
  .mapping-text {
    display: inline-block;
    max-width: 28ch;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: bottom;
  }
  .mapping-text-technical {
    max-width: 36ch;
  }

  .mapping-app-icon {
    flex: 0 0 1.5rem;
    width: 1.5rem;
    height: 1.5rem;
    object-fit: contain;
    border-radius: 0.25rem;
  }

  .mapping-name-cell :global(.text-input) {
    flex: 1;
    min-width: 0;
  }

  .manual-add-form {
    display: flex;
    flex-direction: column;
    gap: var(--control-gap);
  }

  .manual-add-form :global(.text-input) {
    margin-top: 0;
  }

  .menu_actions {
    display: flex;
    justify-content: space-around;
    align-items: center;
  }

  .edited_clearname {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    color: var(--pax-orange);
    font-weight: bold;
  }

  .ai_suggested_value {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: var(--pax-orange);
    font-weight: 700;
  }

  .ai_suggested_value small {
    padding: 0.15rem 0.35rem;
    border-radius: 1rem;
    background: rgba(255, 92, 0, 0.12);
    color: var(--pax-orange);
    font-size: 0.625rem;
    font-weight: 700;
    white-space: nowrap;
  }

  :global(.edited_clearname .tooltip-text) {
    right: auto !important;
  }
</style>
